import React, { startTransition } from 'react';
import { iconComponents } from './IconComponents';
import { clsx } from 'clsx';

export type IconName = keyof typeof iconComponents;

type IconProps = React.HTMLAttributes<SVGSVGElement> & {
	/**
	 * Name of the icon displayed
	 * @type string
	 */
	name: IconName;
	/**
	 * Description of the icon displayed (Accesiblity)
	 * @type string
	 */
	description?: string;
	/**
	 * @deprecated Do not use, use classNames instead
	 */
	color?: string;
	/**
	 * @deprecated Do not use, use classNames instead
	 */
	size?: number | 'full';
	/**
	 * Styles to apply to svg tag.
	 */
	className?: string;
};

function getRem(pixels: number) {
	const result = pixels / 16;
	return `${String(result)}rem`;
}

function getIconSize(size?: number | 'full' | null) {
	if (size === 'full' || !size) {
		return { width: '100%', height: '100%' };
	}
	const remSize = getRem(size);
	return { width: remSize, height: remSize };
}

const InnerIcon = React.forwardRef<SVGSVGElement, IconProps>(
	({ name, size, description, color = 'currentColor', className, ...props }, ref) => {
		const { width, height } = getIconSize(size);

		const LazyComponent: React.LazyExoticComponent<
			React.FunctionComponent<
				React.SVGProps<SVGSVGElement> & {
					title?: string | undefined;
					desc?: string | undefined;
				}
			>
		> = React.useMemo(() => React.lazy(iconComponents[name]()), [name]);

		let lazyComponentElement = null;
		startTransition(() => {
			lazyComponentElement = (
				<LazyComponent
					data-name={name}
					ref={ref}
					fill={color}
					width={width}
					height={height}
					desc={description}
					className={clsx(className)}
					{...props}
				/>
			);
		});

		return <React.Suspense fallback={<div className={clsx(className)}></div>}>{lazyComponentElement}</React.Suspense>;
	}
);
InnerIcon.displayName = 'IconDigitalMemo';

export const Icon = React.forwardRef<SVGSVGElement, IconProps>(({ name, ...props }, ref) => {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (!iconComponents[name]) return <></>;

	return <InnerIcon {...props} name={name} ref={ref} />;
});
Icon.displayName = 'IconDigital';
